import React, { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  Loader2,
  RotateCcw,
  Undo2,
} from 'lucide-react';

const ROWS_PER_PAGE = 100;
const count = (n) => n.toLocaleString('en-US');

export default function RecordResultStep({
  noun,
  nouns,
  where,
  fileName,
  studioName,
  results,
  notes,
  onRetryFailed,
  retrying,
  onUndo,
  undoing,
  onDownloadReport,
  onImportAnother,
  onImportNextSheet,
  nextSheetLabel,
  onChangeStudio,
}) {
  const [rowLimit, setRowLimit] = useState(ROWS_PER_PAGE);
  const amount = (n) => `${count(n)} ${n === 1 ? noun : nouns}`;

  const imported = results.filter((r) => r.status === 'imported');
  const failed = results.filter((r) => r.status === 'failed');
  const skipped = results.filter((r) => r.status === 'skipped');
  const removed = results.filter((r) => r.status === 'removed');
  const undone = removed.length > 0 && imported.length === 0;
  const busy = Boolean(retrying) || undoing;

  let heading = `${amount(imported.length)} imported successfully`;
  let detail = `${studioName} can find them ${where}.`;
  if (undone) {
    heading = `${amount(removed.length)} deleted`;
    detail = `The ${nouns} this import created were deleted from ${studioName}.`;
  } else if (failed.length) {
    heading = `${count(imported.length)} of ${amount(results.length)} imported`;
    detail = `${count(failed.length)} couldn't be saved. Retry ${
      failed.length === 1 ? 'it' : 'them'
    }, or download the import report to see why.`;
  }
  if (!undone && skipped.length) {
    detail += ` ${count(skipped.length)} ${skipped.length === 1 ? 'was' : 'were'} already in the studio and skipped.`;
  }

  const shown = results.slice(0, rowLimit);

  const statusCell = (result) => {
    if (result.status === 'imported') {
      return (
        <span className="pi-chip pi-chip--ok">
          <CheckCircle2 size={15} aria-hidden="true" /> Imported
        </span>
      );
    }
    if (result.status === 'failed') {
      return (
        <div className="pi-failed">
          <span className="pi-chip pi-chip--error">
            <AlertCircle size={15} aria-hidden="true" /> Failed
          </span>
          <span className="pi-failed-reason">{result.error}</span>
        </div>
      );
    }
    return (
      <div className="pi-failed">
        <span className="pi-chip pi-chip--muted">
          {result.status === 'removed' ? 'Removed' : 'Skipped'}
        </span>
        {result.error && <span className="pi-muted pi-status-note">{result.error}</span>}
      </div>
    );
  };

  return (
    <>
      <div className="pi-result-hero">
        <div
          className={`pi-result-icon${undone ? ' is-neutral' : failed.length ? ' is-partial' : ''}`}
        >
          {undone ? (
            <Undo2 size={30} />
          ) : failed.length ? (
            <AlertTriangle size={30} />
          ) : (
            <Check size={34} strokeWidth={3} />
          )}
        </div>
        <h3>{heading}</h3>
        <p>{detail}</p>
        <div className="pi-card pi-file-chip pi-file-chip--center">
          <FileSpreadsheet className="pi-excel-icon" size={22} aria-hidden="true" />
          <span>{fileName}</span>
        </div>
      </div>

      <div className="pi-stats">
        <div className="pi-stat pi-stat--ok">
          <FileText size={30} aria-hidden="true" />
          <div>
            <strong>{count(imported.length)}</strong>
            <span>Imported</span>
          </div>
        </div>
        <div className="pi-stat pi-stat--error">
          <AlertCircle size={30} aria-hidden="true" />
          <div>
            <strong>{count(failed.length)}</strong>
            <span>Failed</span>
          </div>
        </div>
        <div className="pi-stat pi-stat--info">
          <Info size={30} aria-hidden="true" />
          <div>
            <strong>{count(skipped.length)}</strong>
            <span>Already there</span>
          </div>
        </div>
      </div>

      {!undone &&
        notes.map((note) => (
          <div key={note} className="pi-banner pi-banner--info">
            <Info size={18} aria-hidden="true" />
            {note}
          </div>
        ))}

      <section className="pi-card pi-result-card">
        <div className="pi-table-wrap">
          <table className="pi-table pi-result-table">
            <thead>
              <tr>
                <th>Excel row</th>
                <th>{noun.charAt(0).toUpperCase() + noun.slice(1)}</th>
                <th>Import status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((result) => (
                <tr key={result.id}>
                  <td className="pi-muted">{result.sourceRows.join(', ')}</td>
                  <td>{result.label}</td>
                  <td>{statusCell(result)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {results.length > shown.length && (
          <div className="pi-more-rows">
            <span>
              Showing {count(shown.length)} of {count(results.length)}. The import report lists
              every row.
            </span>
            <button
              type="button"
              className="pi-btn pi-btn--outline"
              onClick={() => setRowLimit((limit) => limit + ROWS_PER_PAGE)}
            >
              Show {Math.min(ROWS_PER_PAGE, results.length - shown.length)} more
            </button>
          </div>
        )}
      </section>

      <div className="pi-footer">
        <div className="pi-footer-actions pi-footer-actions--start">
          <button type="button" className="pi-btn pi-btn--outline" onClick={onDownloadReport}>
            <Download size={16} aria-hidden="true" /> Download import report
          </button>
          {imported.length > 0 && (
            <button
              type="button"
              className="pi-btn pi-btn--outline pi-btn--danger"
              onClick={onUndo}
              disabled={busy}
            >
              {undoing ? (
                <>
                  <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Undoing…
                </>
              ) : (
                <>
                  <Undo2 size={16} aria-hidden="true" /> Undo import
                </>
              )}
            </button>
          )}
        </div>
        <div className="pi-footer-actions">
          {failed.length > 0 && (
            <button
              type="button"
              className="pi-btn pi-btn--outline-accent"
              onClick={onRetryFailed}
              disabled={busy}
            >
              {retrying ? (
                <>
                  <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Retrying{' '}
                  {count(retrying.done)} of {count(retrying.total)}…
                </>
              ) : (
                <>
                  <RotateCcw size={16} aria-hidden="true" /> Retry {count(failed.length)} failed
                </>
              )}
            </button>
          )}
          {onImportNextSheet && (
            <button
              type="button"
              className="pi-btn pi-btn--outline-accent"
              onClick={onImportNextSheet}
              disabled={busy}
            >
              {nextSheetLabel}
            </button>
          )}
          <button
            type="button"
            className="pi-btn pi-btn--outline-accent"
            onClick={onImportAnother}
            disabled={busy}
          >
            Import another file
          </button>
          <button
            type="button"
            className="pi-btn pi-btn--primary pi-btn--lg"
            onClick={onChangeStudio}
            disabled={busy}
          >
            Import for another studio
          </button>
        </div>
      </div>
    </>
  );
}
